import { MouseEvent, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { parse } from '@client/common/utils/queryParams';
import { ProductVariantsComponent } from '@client/product/components/ProductData/ProductVariants/index';
import { ProductVariantOptionType, ProductVariantType } from '@client/product/redux/products/types';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useOpenLink, useUrlCreator } from '@client/routes/hooks';
import { getCdnImageUrl, getProductFallbackImgUrl } from '@client/routes/utils/url';

const addThumbnailPlaceholder = (groups: ProductVariantType[]): ProductVariantType[] => groups.map(group => {
  const hasThumbnails = group.options.some(o => !!o.thumbnail);
  const fallbackUrl = hasThumbnails ? getProductFallbackImgUrl() : null;

  return {
    ...group,
    options: group.options.map(option => (
      {
        ...option,
        thumbnail: getCdnImageUrl(option.thumbnail) || fallbackUrl,
      }
    )),
  };
});

interface WithContainerProps {
  sku: string;
  variantGroups: ProductVariantType[];
}

export const ProductVariantsContainer = ({ sku, variantGroups }: WithContainerProps): ReactElement => {
  const location = useLocation();
  const urlCreator = useUrlCreator();
  const openLink = useOpenLink();

  const onSelect = (event: MouseEvent<HTMLInputElement>, option: ProductVariantOptionType): void => {
    if (option.link?.sku === sku) {
      return;
    }

    const urlToProduct = urlCreator(ROUTES.product, { sku: option.link?.sku });
    const isExactMatchPart = !option.link?.match ? '?variants=no-match' : '';

    openLink(event, `${urlToProduct}${isExactMatchPart}`);
  };

  const hasSelectionError = location.search && parse(location.search)?.variants === 'no-match';
  const groupsWithThumbnails = addThumbnailPlaceholder(variantGroups);

  return (
    <ProductVariantsComponent
      // this key is important - without that component doesn't rerender when we go back to previous product
      key={sku}
      variantGroups={groupsWithThumbnails}
      hasSelectionError={hasSelectionError}
      onSelect={onSelect}
    />
  );
};
